import React, {useContext, useEffect, useState, useMemo, useCallback} from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {getPremisesSearchResults} from '../api';
import {AppContext} from '../contexts/AppProvider';
import SearchForm from '../components/Search/SearchForm';
import SearchFilters from '../components/Search/SearchFilters';
import SearchResults from '../components/Search/SearchResults';
import Header from '../components/Header';

const PublicRegister = () => {
    const {context, filterCategories} = useContext(AppContext);
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();

    const [searchResults, setSearchResults] = useState([]);
    const [searchTerm, setSearchTerm] = useState(searchParams.get('query') ?? '');
    const [currentSearchTerm, setCurrentSearchTerm] = useState('');
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState('1');
    const [currentPage, setCurrentPage] = useState('1');
    const [pageRangeFrom, setPageRangeFrom] = useState(0);
    const [pageRangeTo, setPageRangeTo] = useState(0);
    const [filteredSearchPerformed, setFilteredSearchPerformed] = useState(!!searchParams.get('query'));
    const [filtersApplied, setFiltersApplied] = useState(false);
    const [reloadRegister, setReloadRegister] = useState(false);

    const storageFilterName = useMemo(() => `searchFilter${context.id}`, [context]);

    const initialFilters = useMemo(() => {
        const filters = {};
        filterCategories.forEach(category => {
            filters[category.name] = [];
        });
        return filters;
    }, [filterCategories]);

    const [searchFilters, setSearchFilters] = useState(initialFilters);
    const [appliedFilters, setAppliedFilters] = useState(initialFilters);

    const fetchResults = useCallback(async (page, term, filters) => {
        let queryParams = [];

        setSearchResults([]);

        if (term) {
            queryParams.push(`query=${term}`);
        }

        Object.entries(filters).forEach(([key, value]) => {
            if (Array.isArray(value) && value.length > 0) {
                const formattedKey = key.replace(/\s+/g, '');
                queryParams.push(`${formattedKey}=${value.join(',')}`);
            }
        });

        const queryString = queryParams.join('&');

        try {
            const response = await getPremisesSearchResults({
                licenseId: context.id,
                page,
                queryString,
            });

            if (response.status === 422) {
                return fetchResults(1, term, filters);
            }

            localStorage.setItem(storageFilterName, JSON.stringify(filters));

            const newResults = Array.isArray(response.data) ? response.data : [];
            setSearchResults(newResults);

            setTotalRecords(response.meta.total);
            setCurrentPage(response.meta.current_page);
            setTotalPages(response.meta.last_page);
            setPageRangeFrom(response.meta.from);
            setPageRangeTo(response.meta.to);
            setFilteredSearchPerformed(queryParams.length > 0);
            setCurrentSearchTerm(term);

            const urlParams = new URLSearchParams(queryString);
            if (page > 1) {
                urlParams.set('page', page);
            }
            setSearchParams(urlParams);

            return response;
        } catch (error) {
            console.error('Error fetching search results:', error);
        }
    }, [context.id, storageFilterName]);

    const getResults = useCallback((page = 1, term = searchTerm) => {
        if (Object.values(searchFilters).some(arr => arr.length > 0)) {
            setFiltersApplied(true);
        }
        return fetchResults(page, term, searchFilters);
    }, [searchTerm, searchFilters, fetchResults]);

    const searchHandler = useCallback(e => {
        e.preventDefault();
        setCurrentSearchTerm(searchTerm);
        getResults(1);
    }, [searchTerm, getResults]);

    const clearFiltersHandler = useCallback(() => {
        setSearchTerm('');
        setCurrentSearchTerm('');
        setFiltersApplied(false);
        setFilteredSearchPerformed(false);
        setReloadRegister(true);
        setSearchFilters(initialFilters);
        setAppliedFilters(initialFilters);
        localStorage.removeItem(storageFilterName);
    }, [storageFilterName, initialFilters]);

    const pageClickHandler = useCallback(page => {
        const currentPageNumber = parseInt(currentPage, 10);
        const pageNumber = parseInt(page, 10);

        if (pageNumber !== currentPageNumber) {
            getResults(pageNumber);
            window.scrollTo({top: 0, behavior: 'instant'});
        }
    }, [getResults, currentPage]);

    const premisesSelectedHandler = useCallback((e, premisesId) => {
        e.preventDefault();
        navigate(`/premises/${premisesId}`);
    }, [navigate]);

    const removeFilterHandler = useCallback((name, value) => {
        setSearchFilters(prev => ({
            ...prev,
            [name]: prev[name].filter(item => item !== value)
        }));

        setAppliedFilters(prev => ({
            ...prev,
            [name]: prev[name].filter(item => item !== value)
        }));

        setReloadRegister(true);
    }, []);

    const filterSelectedHandler = useCallback((target, label) => {
        const {checked, value} = target;
        setSearchFilters(prev => {
            const newState = {...prev};

            if (!newState[label]) {
                newState[label] = [];
            }

            if (checked) {
                if (!newState[label].includes(value)) {
                    newState[label] = [...newState[label], value];
                }
            } else {
                newState[label] = newState[label].filter(item => item !== value);
            }

            return newState;
        });
    }, []);

    const applyFilterHandler = useCallback(event => {
        event.preventDefault();

        const cleanedFilters = Object.fromEntries(
            Object.entries(searchFilters)
                .map(([key, values]) => [key, [...new Set(values)]])
                .filter(([_, values]) => values.length > 0)
        );

        setAppliedFilters(cleanedFilters);
        setSearchFilters(cleanedFilters);
        localStorage.setItem(storageFilterName, JSON.stringify(cleanedFilters));
        searchHandler(event);
    }, [storageFilterName, searchFilters, searchHandler]);

    useEffect(() => {
        if (reloadRegister) {
            getResults(1);
            setReloadRegister(false);
        }
    }, [reloadRegister, getResults]);

    useEffect(() => {
        const parsedFilters = {...initialFilters};
        let hasUrlFilters = false;

        searchParams.forEach((value, key) => {
            if (key !== 'query' && key !== 'page') {
                const originalKey = key.replace(/([A-Z])/g, ' $1').trim();
                if (originalKey in parsedFilters) {
                    parsedFilters[originalKey] = value.split(',');
                    hasUrlFilters = true;
                }
            }
        });

        const filtersToUse = hasUrlFilters
            ? parsedFilters
            : JSON.parse(localStorage.getItem(storageFilterName)) || initialFilters;

        if (hasUrlFilters) setFiltersApplied(true);
        setSearchFilters(filtersToUse);
        setAppliedFilters(filtersToUse);

        const page = searchParams.get('page') || 1;
        const term = searchParams.get('query') || '';

        fetchResults(page, term, filtersToUse);
    }, []);

    return (
        <main className="ds_layout ds_layout--search-results--filters" style={{minHeight: '600px'}}>
            <Header title="Search" subtitle={context.name}/>

            <div className="ds_layout__content">
                <SearchForm
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    searchHandler={searchHandler}
                />
            </div>

            <div className="ds_layout__sidebar">
                <SearchFilters
                    searchFilters={searchFilters}
                    filterSelectedHandler={filterSelectedHandler}
                    applyFilterHandler={applyFilterHandler}
                    clearFiltersHandler={clearFiltersHandler}
                    filteredSearchPerformed={filteredSearchPerformed}
                />
            </div>

            {(searchResults?.length > 0 || filteredSearchPerformed) && (
                <div className="ds_layout__list">
                    <SearchResults
                        searchResults={searchResults}
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalRecords={totalRecords}
                        pageRangeFrom={pageRangeFrom}
                        pageRangeTo={pageRangeTo}
                        currentSearchTerm={currentSearchTerm}
                        filtersApplied={filtersApplied}
                        filteredSearchPerformed={filteredSearchPerformed}
                        appliedFilters={appliedFilters}
                        pageClickHandler={pageClickHandler}
                        removeFilterHandler={removeFilterHandler}
                        premisesSelectedHandler={premisesSelectedHandler}
                        clearFiltersHandler={clearFiltersHandler}
                    />
                </div>
            )}
        </main>
    );
};

export default PublicRegister;